import React from "react";
import styled from "styled-components";
import { FiStar } from "react-icons/fi";
import Rating from "../../components/UI/Rating";
import { useLanguage } from "../../contexts/LanguageContext";
import { monogram } from "../MVP/home/primitives";

const Hero = styled.section<{ $bg?: string }>`
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) minmax(220px, 300px);
  align-items: center;
  gap: 24px 32px;
  min-width: 0;
  padding: 32px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  ${(p) =>
    p.$bg
      ? `background-image: linear-gradient(90deg, ${p.theme.colors.surface}f2 0%, ${p.theme.colors.surface}b8 60%, ${p.theme.colors.surface}99 100%), url(${JSON.stringify(p.$bg)}); background-size: cover; background-position: center;`
      : ""}
  @media (max-width: 900px) {
    grid-template-columns: auto minmax(0, 1fr);
    padding: 20px;
    gap: 16px 20px;
    .rating {
      grid-column: 1 / -1;
    }
  }
  @media (max-width: 480px) {
    padding: 16px;
    gap: 16px;
  }
`;
const Avatar = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.surfaceHover};
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 34px;
  font-weight: 650;
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  @media (max-width: 480px) {
    width: 72px;
    height: 72px;
    font-size: 26px;
  }
`;
const Who = styled.div`
  min-width: 0;
  display: grid;
  gap: 8px;
  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    min-width: 0;
  }
  h1 {
    font-size: 28px;
    line-height: 36px;
    font-weight: 700;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
    @media (max-width: 767px) {
      font-size: 24px;
      line-height: 32px;
    }
  }
  .premium {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 28px;
    padding: 0 10px;
    border-radius: 6px;
    background: ${(p) => p.theme.colors.primarySoft};
    color: ${(p) => p.theme.colors.primary};
    font-size: 13px;
    font-weight: 650;
    svg {
      width: 14px;
      height: 14px;
    }
  }
  .meta {
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
  }
`;

/** Профильный hero: одинаковый для своего и чужого профиля. H1 — ник игрока. */
export default function PlayerHero({
  name,
  avatar,
  background,
  isPremium,
  elo,
  meta,
  actions,
}: {
  name: string;
  avatar?: string;
  background?: string;
  isPremium?: boolean;
  elo?: number;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  const { t } = useLanguage();
  return (
    <Hero $bg={background}>
      <Avatar aria-hidden="true">{avatar ? <img src={avatar} alt="" /> : monogram(name)}</Avatar>
      <Who>
        <div className="row">
          <h1>{name}</h1>
          {isPremium && (
            <span className="premium">
              <FiStar aria-hidden="true" />
              {t("common.premium")}
            </span>
          )}
        </div>
        {meta && <div className="meta">{meta}</div>}
        {actions && <div className="actions">{actions}</div>}
      </Who>
      {typeof elo === "number" && (
        <div className="rating">
          <Rating elo={elo} />
        </div>
      )}
    </Hero>
  );
}
